/**
 * DOM manipulation utilities
 */

export interface DomUtils {
  create<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    attributes?: Record<string, any>,
    children?: Array<Node | string>
  ): HTMLElementTagNameMap[K];
  find(selectors: string, context?: ParentNode): Element | null;
  findAll(selectors: string, context?: ParentNode): Element[];
  addClass(element: Element, className: string): void;
  removeClass(element: Element, className: string): void;
  toggleClass(element: Element, className: string, force?: boolean): void;
  hasClass(element: Element, className: string): boolean;
  setAttributes(element: Element, attributes: Record<string, any>): void;
  getPosition(element: Element): { top: number; left: number; width: number; height: number };
  isInViewport(element: Element, threshold?: number): boolean;
  debounce<T extends (...args: any[]) => any>(fn: T, wait: number): (...args: Parameters<T>) => void;
  throttle<T extends (...args: any[]) => any>(fn: T, limit: number): (...args: Parameters<T>) => void;
  loadImage(src: string): Promise<HTMLImageElement>;
}

export const dom: DomUtils = {
  create<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    attributes: Record<string, any> = {},
    children: Array<Node | string> = []
  ): HTMLElementTagNameMap[K] {
    const element = document.createElement(tagName);
    
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key.startsWith('on') && typeof value === 'function') {
        const eventName = key.slice(2).toLowerCase();
        element.addEventListener(eventName, value);
      } else if (key in element) {
        (element as any)[key] = value;
      } else {
        element.setAttribute(key, String(value));
      }
    });

    children.forEach(child => {
      if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      } else if (child instanceof Node) {
        element.appendChild(child);
      }
    });

    return element;
  },

  find(selectors: string, context: ParentNode = document): Element | null {
    return context.querySelector(selectors);
  },

  findAll(selectors: string, context: ParentNode = document): Element[] {
    return Array.from(context.querySelectorAll(selectors));
  },

  addClass(element: Element, className: string): void {
    element.classList.add(className);
  },

  removeClass(element: Element, className: string): void {
    element.classList.remove(className);
  },

  toggleClass(element: Element, className: string, force?: boolean): void {
    element.classList.toggle(className, force);
  },

  hasClass(element: Element, className: string): boolean {
    return element.classList.contains(className);
  },

  setAttributes(element: Element, attributes: Record<string, any>): void {
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, String(value));
    });
  },

  getPosition(element: Element): { top: number; left: number; width: number; height: number } {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height
    };
  },

  isInViewport(element: Element, threshold?: number): boolean {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const t = threshold || 0;

    return (
      rect.top >= -t &&
      rect.left >= -t &&
      rect.bottom <= windowHeight + t &&
      rect.right <= windowWidth + t
    );
  },

  debounce<T extends (...args: any[]) => any>(fn: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  },

  throttle<T extends (...args: any[]) => any>(fn: T, limit: number): (...args: Parameters<T>) => void {
    let inThrottle = false;
    let lastArgs: any[] | null = null;

    return (...args: any[]) => {
      if (!inThrottle) {
        fn(...args);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
          if (lastArgs) {
            fn(...lastArgs);
            lastArgs = null;
          }
        }, limit);
      } else {
        lastArgs = args;
      }
    };
  },

  loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }
};
